import styled from "@emotion/styled";
import { AnimatePresence, motion } from "motion/react";
import { useEffect } from "react";
import { Outlet } from "react-router";

import { PAGE_SIDE_PADDING, SearchContextProvider } from "@skilitics/designer-ui-components";
import type { InteractModuleListItem } from "@skilitics/nautilus-endpoint-definition";

import { useWorkspaceId } from "../auth/workspaces.ts";
import { useLandingPageSettings } from "./LandingPageSettingsContext";
import { createByTags, createByTitle, createByUpdatedOn } from "./ordering";
import { useFetchModuleList } from "./routes/modules/modulePersistence";
import { HeaderOutlet } from "./routing/HeaderOutlet";
import { Sidebar } from "./sidebar/Sidebar";
import { useActiveArea } from "./useActiveArea";

const Container = styled.div`
  display: flex;
  flex-direction: row;

  width: 100%;
  height: 100%;

  color: ${({ theme }) => theme.generalTextIcon};

  /* Below the content min-width the whole layout scrolls horizontally rather
     than squashing — we don't target small screens, but they stay usable. */
  overflow-x: auto;
  overflow-y: hidden;

  /* add some variables while adjusting everything */
  --background-color: ${({ theme }) => theme.surfaceVariant};

  --font-size-small: 16px;

  --min-width: ${768}px;

  --page-padding-inline: ${PAGE_SIDE_PADDING}px;
  --page-padding: 25px var(--page-padding-inline);

  background-color: var(--background-color);
`;

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  flex: 1 1 auto;
  height: 100%;
  overflow: hidden;
`;

const ScrollableArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  flex: 1 1 auto;
  width: 100%;
  min-width: var(--min-width);

  overflow-y: scroll;
  overflow-x: hidden;
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;

  flex: 0 0 auto;
  width: 100%;
  overflow: hidden;
  scrollbar-gutter: stable;

  z-index: 1;

  &:empty {
    display: none;
  }
`;

const RouteContainer = styled.div`
  width: 100%;
  height: 100%;

  min-width: var(--min-width);
`;

const OutletContainer = styled.div`
  position: relative;

  width: 100%;
  min-height: 100%;

  &.fade-enter {
    opacity: 0;
    scale: 0.92;
  }

  &.fade-enter-active {
    opacity: 1;
    scale: 1;

    transition:
      opacity 125ms ease-out,
      scale 125ms ease-out;
  }
`;

export const LandingPage = () => {
  const { modules = [], status, isFetching } = useFetchModuleList();
  const { filterSets, ordering, orderingDirection, view } = useLandingPageSettings();
  const area = useActiveArea();
  // Keyed by workspace so switching clears any active search query, rather
  // than leaving the previous workspace's results over the new module list.
  // Ideally, this would be automatically cleared on switching workspace...
  const workspace = useWorkspaceId();

  const createOrderBy =
    ordering === "title" ? createByTitle : ordering === "updatedOn" ? createByUpdatedOn : createByTags;

  const orderedBy = createOrderBy(orderingDirection);

  useEffect(() => {
    document.title = "Skilitics Interact";
  }, []);

  return (
    <Container>
      <Sidebar />
      <SearchContextProvider<InteractModuleListItem>
        key={workspace}
        source={modules}
        filterSets={filterSets}
        ordering={orderedBy}
        status={status}
        isFetching={isFetching}
      >
        <ContentColumn>
          <HeaderContainer>
            <HeaderOutlet />
          </HeaderContainer>
          <ScrollableArea>
            <RouteContainer>
              <AnimatePresence>
                <motion.div
                  key={`${area}:${view}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.125 }}
                >
                  <OutletContainer>
                    <Outlet />
                  </OutletContainer>
                </motion.div>
              </AnimatePresence>
            </RouteContainer>
          </ScrollableArea>
        </ContentColumn>
      </SearchContextProvider>
    </Container>
  );
};
